import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, RefreshCw, Search, ChevronLeft, ChevronRight,
  Repeat, PlayCircle, Trash2, ToggleLeft, ToggleRight,
} from 'lucide-react';

import { usePermission }   from '@/hooks/usePermission';
import { Permissions }     from '@/constants/permissions';
import { AppRoutes }       from '@/constants/routes';
import { Spinner }         from '@/components/ui/Spinner';
import { ConfirmDialog }   from '@/components/ui/ConfirmDialog';
import { cn }              from '@/utils/cn';
import { recurringJournalApi } from '@/api/recurringJournal.api';

// â”€â”€ Types â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
interface RecurringJournalListDto {
  id: string;
  recurringCode: string;
  description: string;
  frequencyType: string;
  frequencyInterval: number;
  nextRunDate?: string;
  lastRunDate?: string;
  isActive: boolean;
  isDue: boolean;
  executionCount: number;
}

interface RecurringJournalFilter {
  page: number;
  pageSize: number;
  search?: string;
  isActive?: boolean;
}

/** The server's own reason where it gave one, else a plain fallback. */
const messageOf = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? fallback;

const STATUS_COLORS: Record<string, string> = {
  Active:   'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
  Inactive: 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-gray-400',
  Paused:   'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
  Completed:'bg-blue-100 dark:bg-blue-900/30  text-blue-700 dark:text-blue-400',
};

// â”€â”€ Page â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export const RecurringJournalListPage = () => {
  const navigate          = useNavigate();
  const { hasPermission } = usePermission();

  const canCreate  = hasPermission(Permissions.RECURRING_JOURNAL_CREATE);
  const canDelete  = hasPermission(Permissions.RECURRING_JOURNAL_DELETE);
  const canExecute = hasPermission(Permissions.RECURRING_JOURNAL_EXECUTE);
  const canEdit    = hasPermission(Permissions.RECURRING_JOURNAL_EDIT);

  const [items,   setItems]   = useState<RecurringJournalListDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [total,   setTotal]   = useState(0);
  const [error,   setError]   = useState<string | null>(null);
  const [page,    setPage]    = useState(1);
  const pageSize = 20;

  const [filter, setFilter] = useState<RecurringJournalFilter>({ page: 1, pageSize });
  const [search, setSearch] = useState('');

  const [deleting, setDeleting] = useState<RecurringJournalListDto | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const load = useCallback(async (f: RecurringJournalFilter) => {
    setLoading(true);
    setError(null);
    try {
      const res  = await recurringJournalApi.getList({
        page: f.page, pageSize: f.pageSize, search: f.search, isActive: f.isActive,
      });
      const data = res.data?.data;
      setItems((data?.items ?? []) as RecurringJournalListDto[]);
      setTotal(data?.totalCount ?? 0);
    } catch (err) {
      // A failed load must not read as "no recurring journals": the two look identical on an empty
      // table, and this screen called a wrong address for a long time without anyone noticing.
      setItems([]);
      setTotal(0);
      setError(messageOf(err, 'Could not load recurring journals.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(filter); }, [filter, load]);

  const handleSearch = () => {
    setFilter(f => ({ ...f, search: search || undefined, page: 1 }));
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setFilter(f => ({ ...f, page: newPage }));
  };

  const handleToggleActive = async (item: RecurringJournalListDto) => {
    setError(null);
    try {
      await recurringJournalApi.setActive(item.id, !item.isActive);
      load(filter);
    } catch (err) {
      setError(messageOf(err, 'Could not change the status.'));
    }
  };

  const handleDelete = (item: RecurringJournalListDto) => setDeleting(item);

  const executeDelete = async () => {
    if (!deleting) return;
    setDeleteLoading(true);
    try {
      await recurringJournalApi.remove(deleting.id);
      setDeleting(null);
      load(filter);
    } catch (err) {
      setDeleting(null);
      setError(messageOf(err, 'Could not delete the recurring journal.'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleExecute = async (id: string) => {
    setError(null);
    try {
      await recurringJournalApi.execute(id);
      load(filter);
    } catch (err) {
      setError(messageOf(err, 'Could not run the recurring journal.'));
    }
  };

  const totalPages = Math.ceil(total / pageSize);
  const fmtDate = (d?: string) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">

      {/* â”€â”€ Header â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
              <Repeat size={20} className="text-teal-600 dark:text-teal-400" />
              Recurring Journals
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              {total.toLocaleString()} templates
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => load(filter)}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-gray-400 transition-colors" title="Refresh">
              <RefreshCw size={16} />
            </button>
            {canCreate && (
              <button
                onClick={() => navigate(AppRoutes.RECURRING_JOURNAL_NEW)}
                className="flex items-center gap-1.5 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                <Plus size={15} />
                New Template
              </button>
            )}
          </div>
        </div>

        {/* â”€â”€ Filters â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
        <div className="flex items-center gap-3 mt-4 flex-wrap">
          <div className="relative flex-1 max-w-xs">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              placeholder="Search code or description..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg
                         bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                         focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
            />
          </div>

          <div className="flex items-center gap-1.5">
            {([
              { label: 'Active',   value: true  },
              { label: 'Inactive', value: false },
            ] as const).map(opt => (
              <button
                key={String(opt.value)}
                onClick={() => {
                  setFilter(f => ({ ...f, isActive: f.isActive === opt.value ? undefined : opt.value, page: 1 }));
                  setPage(1);
                }}
                className={cn(
                  'px-3 py-1.5 text-xs font-medium rounded-full transition-all',
                  filter.isActive === opt.value
                    ? 'bg-teal-600 text-white'
                    : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200'
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* â”€â”€ Table â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <div className="flex-1 overflow-auto">
        {error && (
          <div role="alert" className="mx-6 mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
            {error}
          </div>
        )}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Spinner size="lg" />
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-400">
            <Repeat size={40} className="mb-3 opacity-40" />
            <p className="text-sm font-medium">No recurring journals found</p>
            {canCreate && (
              <button onClick={() => navigate(AppRoutes.RECURRING_JOURNAL_NEW)}
                className="mt-3 text-teal-600 dark:text-teal-400 text-sm hover:underline">
                Create your first template
              </button>
            )}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800/80 backdrop-blur border-b border-slate-200 dark:border-slate-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Code</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Description</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Frequency</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Next Run</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Last Run</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Active</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {items.map(item => (
                <tr key={item.id}
                  onClick={() => canEdit && navigate(AppRoutes.RECURRING_JOURNAL_EDIT.replace(':id', item.id))}
                  className={cn('hover:bg-teal-50 dark:hover:bg-slate-700/50 transition-colors group', canEdit && 'cursor-pointer')}>
                  <td className="px-4 py-3 font-mono font-semibold text-teal-700 dark:text-teal-400">{item.recurringCode}</td>
                  <td className="px-4 py-3 text-slate-700 dark:text-slate-300 max-w-[200px] truncate">{item.description}</td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-400">
                    {item.frequencyType} {item.frequencyInterval > 1 ? `(every ${item.frequencyInterval})` : ''}
                  </td>
                  <td className="px-4 py-3 text-slate-500 dark:text-slate-400 text-xs">{fmtDate(item.nextRunDate)}</td>
                  <td className="px-4 py-3 text-slate-500 dark:text-slate-400 text-xs">{fmtDate(item.lastRunDate)}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', STATUS_COLORS[item.isActive ? 'Active' : 'Inactive'] ?? 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-gray-400')}>
                      {item.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={cn('text-xs font-medium', item.isActive ? 'text-green-600 dark:text-green-400' : 'text-slate-400')}>
                      {item.isActive ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      {canEdit && (
                        <button
                          onClick={() => handleToggleActive(item)}
                          title={item.isActive ? 'Deactivate' : 'Activate'}
                          className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-gray-400 transition-colors"
                        >
                          {item.isActive ? <ToggleRight size={16} className="text-green-600 dark:text-green-400" /> : <ToggleLeft size={16} />}
                        </button>
                      )}
                      {canExecute && item.isActive && (
                        <button
                          onClick={() => handleExecute(item.id)}
                          title="Execute now"
                          className="p-1.5 rounded hover:bg-teal-100 text-teal-600 dark:text-teal-400 transition-colors"
                        >
                          <PlayCircle size={16} />
                        </button>
                      )}
                      {canDelete && (
                        <button
                          onClick={() => handleDelete(item)}
                          title="Delete"
                          className="p-1.5 rounded hover:bg-red-100 text-red-500 transition-colors"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* â”€â”€ Pagination â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-6 py-3">
          <p className="text-sm text-slate-500 dark:text-gray-400">Page {page} of {totalPages} · {total.toLocaleString()} records</p>
          <div className="flex items-center gap-1">
            <button onClick={() => handlePageChange(page - 1)} disabled={page <= 1}
              className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              <ChevronLeft size={16} />
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const n = Math.max(1, Math.min(page - 2, totalPages - 4)) + i;
              return (
                <button key={n} onClick={() => handlePageChange(n)}
                  className={cn('w-8 h-8 text-sm rounded transition-colors',
                    n === page ? 'bg-teal-600 text-white font-semibold' : 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300'
                  )}>{n}</button>
              );
            })}
            <button onClick={() => handlePageChange(page + 1)} disabled={page >= totalPages}
              className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={deleting !== null}
        variant="delete"
        title="Delete Recurring Journal"
        message={<span>Delete recurring journal <strong>{deleting?.recurringCode}</strong>?</span>}
        confirmLabel="Yes, Delete"
        cancelLabel="Cancel"
        loading={deleteLoading}
        onConfirm={executeDelete}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
};

